import type { Conversation } from '@orqforge/shared';

export interface SessionBadges {
  active: Set<string>;
  needsYou: Set<string>;
}

function timeAgo(value: string | Date): string {
  const mins = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function SessionList({
  conversations,
  selectedId,
  badges,
  agentNames,
  onSelect,
  onNew,
}: {
  conversations: Conversation[];
  selectedId: string | null;
  badges: SessionBadges;
  agentNames: Array<{ id: string; name: string }>;
  onSelect: (id: string) => void;
  onNew: () => void;
}) {
  const names = Object.fromEntries(agentNames.map((agent) => [agent.id, agent.name]));
  return (
    <div>
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Sessions</span>
        <button type="button" className="text-xs text-gray-600 dark:text-gray-300" onClick={onNew} data-testid="new-session">New</button>
      </div>
      <div className="mt-1 flex max-h-[560px] flex-col gap-1 overflow-y-auto">
        {conversations.length === 0 && <p className="px-3 py-4 text-xs text-gray-400">No sessions yet. Submit a request to start one.</p>}
        {conversations.map((conversation) => (
          <button
            key={conversation.id}
            type="button"
            className={`w-full rounded-lg border px-3 py-2.5 text-left ${conversation.id === selectedId ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40' : 'border-transparent hover:bg-white dark:hover:bg-gray-800'}`}
            onClick={() => onSelect(conversation.id)}
            data-testid={`session-${conversation.id}`}
          >
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{conversation.title}</span>
              {badges.needsYou.has(conversation.id) ? <span className="rounded bg-amber-100 px-1 text-[10px] text-amber-800">needs you</span> : badges.active.has(conversation.id) ? <span className="rounded bg-blue-100 px-1 text-[10px] text-blue-800">live</span> : null}
            </div>
            <div className="mt-0.5 text-[11px] text-gray-400">{names[conversation.agentId] ? `${names[conversation.agentId]} · ` : ''}{timeAgo(conversation.updatedAt)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
